import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import Button from "../../components/common/Button";
import useLinkedInAccounts, { hideLinkedInConnection, clearLinkedInHidden } from "../../utils/useLinkedInAccounts";
import BrandColorPicker from "../../components/common/BrandColorPicker";
import { saveBrandProfile, getBrandProfile, DEFAULT_BRAND_COLORS } from "../../utils/brandColors";
import { getThemes, createTheme, updateTheme, deleteTheme, toApiColors, fromApiColors, createLogo, updateLogo, deleteLogo, fileToBase64, isSafeImage } from "../../utils/brandApi";
import useLogos, { refreshLogosFor } from "../../utils/useLogos";
import useThemes, { refreshThemes } from "../../utils/useThemes";
import useUserPlan from "../../utils/useUserPlan";
import { getLinkedInState } from "../../utils/linkedinState";
import enFlag from "../../assets/sideBar/english.svg";
import esFlag from "../../assets/sideBar/spanish.svg";
import frFlag from "../../assets/sideBar/french.svg";
import itFlag from "../../assets/sideBar/italian.svg";
import plug from "../../assets/navbar/connect-plug.svg";
import disconnectPlug from "../../assets/navbar/disconnect-plug.svg";

const UserAccountModal = ({ userName, setUserName, userEmail, isOpen, onClose, onBeforeConnect }) => {
  const normalizeLang = (lang) => lang?.split("-")[0];
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState( normalizeLang(i18n.language) || "en");
  const [name, setName] = useState(userName);
  const [surname, setSurname] = useState("");
  const { isConnected: isLinkedInConnected, accounts } = useLinkedInAccounts(userEmail);
  const [isCreatingTheme, setIsCreatingTheme] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [themeName, setThemeName] = useState("");
  const [themeColors, setThemeColors] = useState(DEFAULT_BRAND_COLORS);
  const [themeError, setThemeError] = useState("");
  const [isSavingTheme, setIsSavingTheme] = useState(false);
  const { logos, loading: logosLoading, error: logosLoadError, refresh: refreshLogos } = useLogos(userEmail);
  const [logosError, setLogosError] = useState("");
  const logoColorRef = useRef({});
  const logoNegativeRef = useRef({});
  const { themes, loading: themesLoading, error: themesLoadError } = useThemes(userEmail);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  const userPlan = useUserPlan(); 
  const QUOTA = userPlan === "premium" ? 5 : 2;
  const LOGO_QUOTA = userPlan === "premium" ? 5 : 1;

  const languages = [
    { code: "en", label: "English", flag: enFlag },
    { code: "es", label: "Spanish", flag: esFlag },
    { code: "fr", label: "French", flag: frFlag },
    { code: "it", label: "Italian", flag: itFlag },
  ];

  const handleLanguageChange = (code) => {
    setLanguage(code);
    i18n.changeLanguage(code);
  };

  const handleConnectLinkedIn = async () => {
    clearLinkedInHidden(userEmail);

    // The parent may need to persist work before we leave the app.
    if (onBeforeConnect) {
      const ok = await onBeforeConnect();
      if (ok === false) return;
    }

    // Signed state issued by the backend (falls back to the plain email if it cannot be obtained).
    const state = await getLinkedInState(userEmail);

    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_LINKEDIN_REDIRECT_URI;
    const scope = "r_basicprofile w_member_social w_organization_social r_organization_social rw_organization_admin r_member_postAnalytics";
    const url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const profile = getBrandProfile();
    setName(profile?.firstName || userName || "");
    setSurname(profile?.lastName || "");
    setLanguage(normalizeLang(i18n.language) || "en");
  }, [isOpen, userName, i18n.language]);

  if (!isOpen) return null;

  const openCreate = () => {
    setThemeName("");
    setThemeColors(DEFAULT_BRAND_COLORS);
    setThemeError("");
    setEditingTheme(null);
    setIsCreatingTheme(true);
  };

  const openEdit = (theme) => {
    setThemeName(theme.name);
    setThemeColors(fromApiColors(theme));
    setThemeError("");
    setEditingTheme(theme);
    setIsCreatingTheme(true);
  };

  const cancelThemeForm = () => {
    setIsCreatingTheme(false);
    setEditingTheme(null);
    setThemeError("");
  };

  const saveTheme = async () => {
    if (isSavingTheme) return;
    if (!themeName.trim()) { setThemeError(t("userAccountModal.themes.nameRequired")); return; }
    setThemeError("");
    setIsSavingTheme(true);
    try {
      if (editingTheme) {
        await updateTheme(userEmail, editingTheme.theme_id, {
          name: themeName,
          ...toApiColors(themeColors),
        });
      } else {
        await createTheme(userEmail, userPlan, {
          name: themeName,
          ...toApiColors(themeColors),
        });
      }
      await refreshThemes(userEmail);
      cancelThemeForm();
    } catch (err) {
      setThemeError(err.message || t("userAccountModal.themes.saveError"));
    } finally {
      setIsSavingTheme(false);
    }
  };

  const handleDeleteTheme = async (theme_id) => {
    try {
      await deleteTheme(userEmail, theme_id);
      await refreshThemes(userEmail);
    } catch (err) {
      setThemeError(err.message);
    }
  };

  const getLogoForTarget = (target) => logos.find((l) => l.target === target) || null;

  const handleLogoUpload = async (target, which, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setLogosError(t("userAccountModal.logos.tooLarge")); return; }
    if (file.type !== "image/png") { setLogosError(t("userAccountModal.logos.pngOnly")); return; }
    setLogosError("");
    setUploadingSlot(`${target}:${which}`);
    try {
      const base64 = await fileToBase64(file);
      const existing = getLogoForTarget(target);
      const payload = which === "color" ? { logo_color: base64 } : { logo_negative: base64 };
      if (existing) {
        await updateLogo(userEmail, target, payload);
      } else {
        await createLogo(userEmail, userPlan, target, payload);
      }
      await refreshLogosFor(userEmail);
    } catch (err) {
      setLogosError(err.message || t("userAccountModal.logos.saveError"));
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleLogoDelete = async (target, which) => {
    setLogosError("");
    try {
      await deleteLogo(userEmail, target, which);
      await refreshLogosFor(userEmail);
    } catch (err) {
      setLogosError(err.message);
    }
  };

  const renderLogoSlot = (account, which, logo) => {
    const src = which === "color" ? logo?.logo_color : logo?.logo_negative;
    const inputRef = which === "color" ? logoColorRef : logoNegativeRef;
    const isUploading = uploadingSlot === `${account.target}:${which}`;
    const atQuota = logos.length >= LOGO_QUOTA && !logo;
    const isColor = which === "color";

    return (
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold uppercase tracking-wide text-black-400">
          {t(isColor ? "userAccountModal.logos.colorVersion" : "userAccountModal.logos.negativeVersion")}
        </label>

        {isUploading ? (
          <div className="w-full h-20 border-2 border-dashed border-primary-300 rounded-lg flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-bold text-black-600">
              {t("userAccountModal.logos.uploading")}
            </span>
          </div>
        ) : src ? (
          <div className="flex flex-col gap-2">
            <div className={`w-full h-20 border border-gray-200 rounded-lg flex items-center justify-center p-2 ${isColor ? "bg-white" : "bg-black-800"}`}>
              {isSafeImage(src) ? (
                <img src={src} alt={`${which} logo`} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className={`text-xs font-bold ${isColor ? "text-black-600" : "text-white"}`}>
                  ✓ {t("userAccountModal.logos.uploaded")}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current[account.target]?.click()}
                className="text-xs font-bold underline text-black-800 hover:text-black"
              >
                {t("buttons.replace")}
              </button>
              <button
                type="button"
                onClick={() => handleLogoDelete(account.target, which)}
                className="text-xs font-bold text-red-500 hover:text-red-700"
              >
                {t("buttons.delete")}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => !atQuota && inputRef.current[account.target]?.click()}
            disabled={atQuota}
            className="w-full h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-xs text-black-400 font-bold hover:border-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            + {t("userAccountModal.logos.upload")}
          </button>
        )}

        <input
          ref={(el) => { inputRef.current[account.target] = el; }}
          type="file"
          accept="image/png"
          className="hidden"
          onChange={(e) => handleLogoUpload(account.target, which, e.target.files?.[0])}
        />
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal Wrapper */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Modal */}
        <div
          className="
            w-full max-w-7xl 
            mx-4 sm:mx-6 lg:mx-8
            max-h-[90vh] overflow-y-auto
            bg-white rounded-2xl shadow-xl border border-gray-200
            p-4 sm:p-6
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold">{t("userAccountModal.title")}</h2>

            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </div>

          {/* Banner */}
          <div className="pt-4">
            <div className="bg-link-100 text-sm sm:text-base lg:text-xl text-black-800 px-3 py-2 rounded">
              {t("userAccountModal.description")}
            </div>
          </div>

          {/* Body */}
          <div className="py-4 space-y-6">
            {/* Profile Info */}
            <div className="border-b pb-4">
              <h3 className="text-lg font-bold mb-4">{t("userAccountModal.profileInfo.title")}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm sm:text-lg font-bold block mb-1">
                    {t("userAccountModal.profileInfo.name")}
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("userAccountModal.profileInfo.name")}
                    className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>

                <div>
                  <label className="text-sm sm:text-lg font-bold block mb-1">
                    {t("userAccountModal.profileInfo.surname")}
                  </label>
                  <input
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    placeholder={t("userAccountModal.profileInfo.surname")}
                    className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>

                <div>
                  <label className="text-sm sm:text-lg font-bold block mb-1">
                    {t("userAccountModal.profileInfo.email")}
                  </label>
                  <input
                    type="email"
                    value={userEmail || ""}
                    readOnly
                    className="border rounded px-4 py-2.5 w-full outline-none bg-black-20 text-black-600"
                  />
                </div>
              </div>
            </div>

            {/* Brand Manual — Themes */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-bold">
                    {t("userAccountModal.themes.title")}
                  </h3>
                  <p className="text-sm text-black-400">
                    {themes.length} {t("userAccountModal.themes.of")} {QUOTA} {t("userAccountModal.themes.used")}
                  </p>
                </div>
                {!isCreatingTheme && (
                  <Button
                    variant="ghost"
                    onClick={openCreate}
                    disabled={themes.length >= QUOTA}
                    className="flex-shrink-0"
                  >
                    + {t("userAccountModal.themes.add")}
                  </Button>
                )}
              </div>

              {(themeError || themesLoadError) && (
                <p className="text-sm text-red-500 mb-3">{themeError || themesLoadError}</p>
              )}

              {/* Theme list */}
              {themesLoading ? (
                <p className="text-sm text-black-400">{t("userAccountModal.themes.loading")}</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {themes.map((theme) => (
                    <div
                      key={theme.theme_id}
                      className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 bg-black-0"
                    >
                      <span className="flex gap-1 flex-shrink-0">
                        {[theme.background, theme.text, theme.highlight].map((c, i) => (
                          <span key={i} className="w-4 h-4 rounded-full border border-black-40" style={{ background: c }} />
                        ))}
                      </span>
                      <span className="flex-1 font-bold text-sm text-black-800">{theme.name}</span>
                      <button
                        type="button"
                        onClick={() => openEdit(theme)}
                        className="text-xs text-black-400 hover:text-black-800 font-bold underline flex-shrink-0"
                      >
                        {t("userAccountModal.themes.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTheme(theme.theme_id)}
                        className="text-xs text-red-500 hover:text-red-700 font-bold flex-shrink-0"
                      >
                        {t("buttons.delete")}
                      </button>
                    </div>
                  ))}
                  {themes.length === 0 && !isCreatingTheme && (
                    <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
                      {t("userAccountModal.themes.empty")}
                    </span>
                  )}
                </div>
              )}

              {/* Create / Edit form */}
              {isCreatingTheme && (
                <div className="mt-2 p-4 bg-black-20 border border-gray-200 rounded-xl space-y-4">
                  <h4 className="font-bold text-black-800">
                    {editingTheme ? t("userAccountModal.themes.editTitle") : t("userAccountModal.themes.newTitle")}
                  </h4>

                  {/* Name input */}
                  <div>
                    <label className="text-sm font-bold block mb-1">
                      {t("userAccountModal.themes.name")}
                    </label>
                    <input
                      type="text"
                      value={themeName}
                      onChange={(e) => setThemeName(e.target.value)}
                      placeholder={t("userAccountModal.themes.namePlaceholder")}
                      className="border rounded px-4 py-2.5 w-full outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>

                  {/* Color picker */}
                  <BrandColorPicker colors={themeColors} onChange={setThemeColors} />

                  {themeError && (
                    <p className="text-sm text-red-500">{themeError}</p>
                  )}

                  <div className="flex gap-3 justify-end">
                    <Button variant="ghost" onClick={cancelThemeForm} disabled={isSavingTheme}>
                      {t("buttons.cancel")}
                    </Button>
                    <Button variant="dark" onClick={saveTheme} disabled={isSavingTheme}>
                      {isSavingTheme && (
                        <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                      )}
                      {t("buttons.saveChanges")}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Brand Logos */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-bold">
                    {t("userAccountModal.logos.title")}
                  </h3>
                  <p className="text-sm text-black-400">
                    {logos.length} {t("userAccountModal.themes.of")} {LOGO_QUOTA} {t("userAccountModal.logos.used")}
                  </p>
                </div>
              </div>

              {(logosError || logosLoadError) && (
                <p className="text-sm text-red-500 mb-3">{logosError || logosLoadError}</p>
              )}

              {!isLinkedInConnected ? (
                <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
                  {t("userAccountModal.logos.connectFirst")}
                </span>
              ) : logosLoading ? (
                <p className="text-sm text-black-400">{t("userAccountModal.logos.loading")}</p>
              ) : (
                <div className="space-y-4">
                  {accounts.map((account) => {
                    const logo = getLogoForTarget(account.target);
                    const atQuota = logos.length >= LOGO_QUOTA && !logo;

                    return (
                      <div key={account.target} className="p-3 bg-black-20 border border-gray-200 rounded-xl space-y-3">
                        <p className="text-sm font-bold text-black-800">{account.target}</p>

                        <div className="grid grid-cols-2 gap-3">
                          {renderLogoSlot(account, "color", logo)}
                          {renderLogoSlot(account, "negative", logo)}
                        </div>

                        {logo && (
                          <button
                            type="button"
                            onClick={() => handleLogoDelete(account.target, "both")}
                            className="text-xs font-bold text-red-500 hover:text-red-700"
                          >
                            {t("userAccountModal.logos.deleteAll")}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Language */}
            <div className="border-b pb-4">
              <h3 className="font-bold text-lg mb-4">{t("userAccountModal.language")}</h3>

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                {languages.map((lang) => {
                  const isSelected = language === lang.code;

                  return (
                    <Button
                      key={lang.code}
                      variant={isSelected ? "languageActive" : "language"}
                      onClick={() => handleLanguageChange(lang.code)}
                      className="w-full sm:w-[200px] flex items-center gap-2 py-4"
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                      />
                      {lang.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Social */}
            <div>
              <h3 className="text-base sm:text-lg mb-4">
                <b>{t("userAccountModal.social")}</b> - {t("userAccountModal.connectSocialAccounts")}
              </h3>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-3">
                {isLinkedInConnected ? (
                  <Button
                    variant="disconnect"
                    onClick={() => {
                      hideLinkedInConnection(userEmail);
                      window.location.reload();
                    }}
                    className="w-full sm:w-auto justify-center"
                  >
                    <img src={disconnectPlug} alt="disconnect" className="w-5 h-5" />
                    {t("buttons.disconnectLinkedIn")}
                  </Button>
                ) : (
                  <Button
                    variant="linkedin"
                    onClick={handleConnectLinkedIn}
                    className="w-full sm:w-auto"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452H17.21v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.985V9h3.105v1.561h.044c.432-.818 1.49-1.681 3.067-1.681 3.28 0 3.885 2.159 3.885 4.964v6.608zM5.337 7.433a1.805 1.805 0 1 1 0-3.61 1.805 1.805 0 0 1 0 3.61zM6.955 20.452H3.72V9h3.235v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    {t("buttons.connectLinkedIn")}
                  </Button>
                )}
              </div>
              <span className="text-sm sm:text-lg bg-secondary-100 px-2 py-1 inline-block">
                {t("userAccountModal.moreSocialNetworks")}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 text-sm sm:text-lg font-bold">
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              {t("buttons.cancel")}
            </Button>

            <Button
              variant="dark"
              onClick={() => {
                saveBrandProfile({ firstName: name, lastName: surname });
                setUserName([name, surname].filter(Boolean).join(" "));
                onClose();
              }}
              className="w-full sm:w-auto"
            >
              {t("buttons.saveChanges")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserAccountModal;
